import React from 'react';
import styled from 'styled-components';
import { CampaignProcessSummary, CampaignProcessStatus } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { formatCampaignDateTime } from '../campaignsUtils';

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const SectionTitle = styled.h3`
    margin: 0 0 var(--spacer-XS) 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-body);
`;

const processCardBase = `
    padding: var(--spacer-S);
    border-radius: var(--spacer-XS);
    background: var(--bg-light);
`;

const ProcessCard = styled.div`
    ${processCardBase}
    border: var(--border-thin) solid var(--appLigthGrey);
`;

const ProcessCardLatest = styled.div`
    ${processCardBase}
    border: var(--border-thin) solid var(--appLigthGrey);
    border-left: var(--border-thick) solid var(--appGreen);
`;

const ProcessHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacer-S);
    flex-wrap: wrap;
    margin-bottom: var(--spacer-XS);
`;

const ProcessDate = styled.span`
    font-weight: 700;
    color: var(--text-body);
`;

const ProcessMeta = styled.span`
    color: var(--text-muted);
    font-size: 0.85rem;
`;

const ProcessCounts = styled.div`
    display: flex;
    gap: var(--spacer-M);
    flex-wrap: wrap;
    font-size: 0.9rem;
    color: var(--text-body);
`;

const getProcessStatusKey = (status: CampaignProcessStatus): TK => {
    switch (status) {
        case CampaignProcessStatus.Done: return TK.finished;
        case CampaignProcessStatus.Sending: return TK.sending;
        case CampaignProcessStatus.Created: return TK.created;
        case CampaignProcessStatus.Error: return TK.error;
        case CampaignProcessStatus.Cancelled: return TK.canceled;
        default: return TK.unknown;
    }
};

interface CampaignProcessTimelineProps {
    processes: CampaignProcessSummary[];
}

export const CampaignProcessTimeline: React.FC<CampaignProcessTimelineProps> = ({ processes }) => {
    const t = useTranslations();

    const renderCard = (process: CampaignProcessSummary, isLatest: boolean) => {
        const Card = isLatest ? ProcessCardLatest : ProcessCard;
        return (
            <Card key={process.id}>
                <ProcessHeader>
                    <div>
                        <ProcessDate>{formatCampaignDateTime(process.startedAt ?? process.creationDate)}</ProcessDate>
                        {process.completedAt && (
                            <ProcessMeta> → {formatCampaignDateTime(process.completedAt)}</ProcessMeta>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--spacer-XS)', alignItems: 'center' }}>
                        <ProcessMeta>{process.createdBy}</ProcessMeta>
                        <ProcessMeta>{t(getProcessStatusKey(process.status))}</ProcessMeta>
                    </div>
                </ProcessHeader>
                <ProcessCounts>
                    <span>{t(TK.recipients)}: <strong>{process.recipientCount}</strong></span>
                    <span>{t(TK.sent)}: <strong>{process.sentCount}</strong></span>
                    <span>{t(TK.failed)}: <strong>{process.failedCount}</strong></span>
                    {process.orderId && (
                        <span style={{ color: 'var(--appGreen)' }}>
                            {t(TK.campaignAnalyticsOrderRef)}: <strong>{process.orderId.slice(-8)}</strong>
                            {process.orderTotal != null && (
                                <> — {t(TK.campaignAnalyticsOrderAmount)}: <strong>{process.orderTotal.toFixed(2)} {process.orderCurrency ?? 'EUR'}</strong></>
                            )}
                        </span>
                    )}
                </ProcessCounts>
            </Card>
        );
    };

    return (
        <Section>
            <SectionTitle>{t(TK.campaignAnalyticsSendHistory)}</SectionTitle>
            {!processes.length ? (
                <div style={{ color: 'var(--text-muted)' }}>{t(TK.campaignNeverSent)}</div>
            ) : (
                processes.map((process, index) => renderCard(process, index === 0))
            )}
        </Section>
    );
};
